import React from 'react';
import MedicalHistoryDetails from '../../_components/organisms/MedicalHistoryDetails';
import { numberToCurrencyFormatter } from './helper';
import {
  formatMemberClaim,
  getTextWithToolTip,
} from './riskPatientDetailsHelper';
import { MonthMap } from './EditDuration';
import moment from 'moment';
import { MemberValues } from './summaryReportHelper';
import { Chip } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';

const tiers = ['Tier 1', 'Tier 2', 'Tier 3', 'Tier 4', 'Tier 5'];

export const titleAndSubtitles = {
  chronic: {
    title: 'Chronic Condition Comorbidity',
    subTitle:
      'Categorizes members into five risk tiers based on the number of chronic conditions they have, from 6+ conditions in the highest risk tier to no chronic conditions in the lowest risk tier.',
  },
  trigger: {
    title: 'Trigger Condition Comorbidity',
    subTitle:
      'Categorizes members into five risk tiers based on trigger conditions identified by the SIIA, with the highest risk tier for those with multiple trigger conditions and the lowest risk tier for those with none.',
  },
  cost: {
    title: 'Cost Percentile',
    subTitle:
      'Tiers members based on their healthcare costs, with the highest tier representing the top 1 percentile of spenders and the lowest tier representing the bottom 60 percentile.',
  },
  utilization: {
    title: 'High Cost Services Utilization',
    subTitle:
      'Categorizes members based on their utilization of high-cost services such as emergency care, urgent care, and surgeries, with the highest tier for those using more than 5 services and the lowest tier for those using none.',
  },
  drugs: {
    title: 'Multiple Drug Consumption',
    subTitle:
      'Tiers members based on the number of different medications they are taking simultaneously, identifying those with the highest medication usage for targeted intervention',
  },
};

const chronicConditionsLabels = [
  {
    title: 'Highly Complex Group (6+ chronic conditions)',
    subTitle:
      'Implement intensive care management for the most complex and high-risk conditions leading to potential stoploss claims.',
  },
  {
    title: 'High Risk Group (4-5 chronic conditions)',
    subTitle:
      'Focus on risk reduction strategies and regular monitoring to prevent the development of further chronic conditions.',
  },
  {
    title: 'Rising Risk Group (2-3 chronic conditions)',
    subTitle:
      'Emphasize preventive measures and early intervention to avoid escalation to high-risk status.',
  },
  {
    title: 'Moderate Risk Group (1 chronic condition)',
    subTitle:
      'Support condition management and health maintenance for individuals with a single chronic condition.',
  },
  {
    title: 'Low Risk Group (0 chronic conditions)',
    subTitle:
      'Promote preventive care and healthy lifestyle choices for individuals with no identified chronic conditions.',
  },
];

const triggerConditionsLabels = [
  {
    title: 'Highly Complex Group (6+ trigger conditions)',
    subTitle:
      'Implement intensive care management for the most complex and high-risk conditions leading to potential stoploss claims.',
  },
  {
    title: 'High Risk Group (4-5 trigger conditions)',
    subTitle:
      'Focus on risk reduction strategies and regular monitoring to prevent the development of further trigger conditions.',
  },
  {
    title: 'Rising Risk Group (2-3 trigger conditions)',
    subTitle:
      'Emphasize preventive measures and early intervention to avoid escalation to high-risk status.',
  },
  {
    title: 'Moderate Risk Group (1 trigger condition)',
    subTitle:
      'Support condition management and health maintenance for individuals with a single trigger condition.',
  },
  {
    title: 'Low Risk Group (0 trigger conditions)',
    subTitle:
      'Promote preventive care and healthy lifestyle choices for individuals with no identified trigger conditions.',
  },
];

const costPercentileLabels = [
  {
    title: 'Top 1 Percentile (Highest Cost)',
    subTitle:
      'Implement cost management strategies and intensive care coordination for the highest spenders.',
  },
  {
    title: 'Next 4 Percentile (High Cost)',
    subTitle:
      'Focus on cost reduction and monitoring for high-cost members to manage expenses effectively.',
  },
  {
    title: 'Next 15 Percentile (Moderate Cost)',
    subTitle:
      'Emphasize cost management and preventive care to maintain moderate spending levels.',
  },
  {
    title: 'Next 40 Percentile (Low Cost)',
    subTitle:
      'Support cost-effective care and health maintenance for low-cost members.',
  },
  {
    title: 'Bottom 60 Percentile (Lowest Cost)',
    subTitle:
      'Promote preventive care and healthy lifestyle choices for the majority of the population with the lowest costs.',
  },
];

const highCostUtilizationLabels = [
  {
    title: 'Very High Utilization (4+ high cost services)',
    subTitle:
      'Implement intensive case management and frequent monitoring to manage high utilization and associated costs.',
  },
  {
    title: 'High Utilization (3 high cost services)',
    subTitle:
      'Focus on reducing unnecessary high-cost service usage through targeted interventions.',
  },
  {
    title: 'Moderate Utilization (2 high cost services)',
    subTitle:
      'Emphasize monitoring and preventive care to prevent an increase in high-cost service usage.',
  },
  {
    title: 'Low Utilization (1 high cost service)',
    subTitle:
      'Support efficient use of healthcare services and promote preventive care.',
  },
  {
    title: 'No Utilization (0 high cost services)',
    subTitle:
      'Encourage preventive care and healthy lifestyle choices to maintain low utilization levels.',
  },
];

const multipleDrugsConsumptionLabels = [
  {
    title: 'Very High Drug Consumption (5+ medications)',
    subTitle:
      'Implement medication management and review programs to ensure appropriate use and avoid polypharmacy risks',
  },
  {
    title: 'High Drug Consumption (4 medications)',
    subTitle:
      'Focus on adherence and monitoring to prevent adverse drug interactions and manage health effectively',
  },
  {
    title: 'Moderate Drug Consumption (3 medications)',
    subTitle:
      'Implement intensive case management and frequent monitoring to manage high utilization and associated costs',
  },
  {
    title: 'Low Drug Consumption (2 medication)',
    subTitle:
      'Support adherence and promote preventive care to manage health with minimal medication',
  },
  {
    title: 'No Drug Consumption (0-1 medications)',
    subTitle:
      'Encourage preventive care and healthy lifestyle choices to avoid the need for medications',
  },
];

export const getLabels = (type: string) => {
  switch (type) {
    case 'chronic':
      return chronicConditionsLabels;
    case 'trigger':
      return triggerConditionsLabels;
    case 'cost':
      return costPercentileLabels;
    case 'utilization':
      return highCostUtilizationLabels;
    case 'drugs':
      return multipleDrugsConsumptionLabels;
    default:
      return chronicConditionsLabels;
  }
};

export const formatRiskStratificationData = (data: any, type: string) => {
  // Template array with default values for each tier
  const template = tiers.map((tier, index) => ({
    memberIds: [],
    count: 0,
    tier: index + 1,
    ...getLabels(type)[index],
  }));

  // Create a map to quickly find and replace values
  const dataMap = new Map();
  data?.forEach((item: any) => {
    if (tiers.includes(item?._id)) {
      const tierIndex = tiers.indexOf(item._id);
      dataMap.set(tierIndex, {
        memberIds: item?.member_unique_id_list,
        count: item?.member_unique_id_list?.length,
        tier: tierIndex + 1,
        rawTier: item._id,
        ...getLabels(type)[tierIndex],
      });
    }
  });

  // Merge the template with the actual data
  const result = template.map((tier, index) => dataMap.get(index) || tier);

  // Sort the result by tier
  return result.sort((a: any, b: any) => a.tier - b.tier);
};

export const getEnrollemntStatusChip = (status: string) => {
  switch (status) {
    case 'Enrolled':
      return (
        <Chip
          label={
            <Typography
              fontSize="12px"
              lineHeight="15px"
              fontType="medium"
              textColor={patientAccessTheme.palette.AIQChips?.textColor2}
            >
              {status}
            </Typography>
          }
          backgroundColor={patientAccessTheme.palette.AIQChips?.background2}
          border="0.3px solid rgba(0, 130, 114, 0.5)"
          borderRadius="4px"
          height="25px"
        />
      );
    case 'Terminated':
      return (
        <Chip
          label={
            <Typography
              fontSize="12px"
              lineHeight="15px"
              fontType="medium"
              textColor={patientAccessTheme.palette.AIQChips?.textColor1}
            >
              {status}
            </Typography>
          }
          backgroundColor={patientAccessTheme.palette.AIQChips?.background1}
          border="0.3px solid rgba(226, 84, 84, 0.5)"
          borderRadius="4px"
          height="25px"
        />
      );
    default:
      return '-';
  }
};

export const formatMembersListsData = (data: any, type?: string) => {
  const tableData: any[] = [];

  data?.forEach((item: any) => {
    const eachRow: any[] = [];
    eachRow.push(item?.member_unique_id);
    eachRow.push(getEnrollemntStatusChip(item?.enrollment_status));
    if (item.rx_utilization) {
      eachRow.push(item.rx_utilization);
    }

    const conditions =
      type === 'trigger' ? item?.trigger_conditions : item?.chronic_conditions;

    const costliestDrugs = item?.costliest_drugs?.length
      ? getTextWithToolTip(
          item.costliest_drugs.map(
            (drug: any) =>
              `${drug?.drug_name}(${numberToCurrencyFormatter(
                drug?.totalPaid,
              )})`,
          ),
          30,
        )
      : '-';

    eachRow.push(conditions?.length ? getTextWithToolTip(conditions, 30) : '-');
    eachRow.push(costliestDrugs);
    eachRow.push(
      getTextWithToolTip(
        `${numberToCurrencyFormatter(
          item?.cost,
          true,
        )}(${numberToCurrencyFormatter(
          item?.total_med_paid,
          true,
        )} + ${numberToCurrencyFormatter(item?.total_rx_paid, true)})`,
        20,
      ),
    );
    eachRow.push(
      getTextWithToolTip(
        `${numberToCurrencyFormatter(
          item?.previous_year_cost,
          true,
        )}(${numberToCurrencyFormatter(
          item?.prev_total_med_paid,
          true,
        )} + ${numberToCurrencyFormatter(item?.prev_total_rx_paid, true)})`,
        20,
      ),
    );
    eachRow.push(
      <MedicalHistoryDetails
        riskPatientDetails={formatMemberClaim(
          item?.claimData,
          item?.member_unique_id,
        )}
      />,
    );

    tableData.push(eachRow);
  });

  return tableData;
};

export const formatDrugDetailsData = (data: any) => {
  const tableData: any[] = [];

  data?.forEach((item: any) => {
    const eachRow: any[] = [];

    eachRow[0] = item?.rx_count;
    eachRow[1] = item?.member_ids?.length ?? 0;

    eachRow[2] = numberToCurrencyFormatter(item?.total_rx_cost ?? 0);

    eachRow[3] = numberToCurrencyFormatter(item?.rx_pmpm?.pmpm ?? 0);

    tableData.push(eachRow);
  });

  return tableData;
};

export const getServiceStartYear = (month: string, year: number) => {
  const monthNumber = MonthMap.get(month)?.monthNumber;
  const defaultDate = new Date(monthNumber + '-01-' + year);

  const startYear = moment(defaultDate).subtract(11, 'months').year();

  return startYear;
};

export const formatMembers = (data: any) => {
  const memberValues: MemberValues = {
    members: '0',
    employees: '0',
    dependents: '0',
  };

  let totalMembers = 0;

  data?.forEach((item: any) => {
    const count = item?.count || 0;

    if (item?.member_relationship_desc === 'Dependent') {
      memberValues.dependents = count.toString();
    } else if (item?.member_relationship_desc === 'Employee') {
      memberValues.employees = count.toString();
    }

    totalMembers += count;
  });

  memberValues.members = totalMembers.toString();
  return memberValues;
};
